import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import ErrorBoundary from "./ErrorBoundary";
import Masthead from "./Masthead";
import OrgSwitcher from "./OrgSwitcher";
import { ChevronDown, LogOut } from "./icons";
import { dateFormatExample, initials } from "./format";
import { byView, canSee, family, pathFor, viewFromPath, type View } from "./routes";
import { Loading, Segmented } from "./parts";
import { langLabel, setLocale, setOverrides, t, tx, useLocale } from "../i18n";
import { locale, type MyLocale } from "../api/locale";
import { PwaBanner } from "../pwa/PwaUi";
import AiAssistant from "./ai/AiAssistant";

const DashboardWorkspace = lazy(() => import("./workspaces/DashboardWorkspace"));
const LeadsWorkspace = lazy(() => import("./workspaces/LeadsWorkspace"));
const OnboardingWorkspace = lazy(() => import("./workspaces/OnboardingWorkspace"));
const MeetingsWorkspace = lazy(() => import("./workspaces/MeetingsWorkspace"));
const CampaignsWorkspace = lazy(() => import("./workspaces/CampaignsWorkspace"));
const AnnouncementsWorkspace = lazy(() => import("./workspaces/AnnouncementsWorkspace"));
const CsiWorkspace = lazy(() => import("./workspaces/CsiWorkspace"));
const PartnersWorkspace = lazy(() => import("./workspaces/PartnersWorkspace"));
const BillingWorkspace = lazy(() => import("./workspaces/BillingWorkspace"));
const SubscriptionsWorkspace = lazy(() => import("./workspaces/SubscriptionsWorkspace"));
const CustomersWorkspace = lazy(() => import("./workspaces/CustomersWorkspace"));
const ClientsWorkspace = lazy(() => import("./workspaces/ClientsWorkspace"));
const ItemsWorkspace = lazy(() => import("./workspaces/ItemsWorkspace"));
const PurchasesWorkspace = lazy(() => import("./workspaces/PurchasesWorkspace"));
const VendorsWorkspace = lazy(() => import("./workspaces/VendorsWorkspace"));
const ExpensesWorkspace = lazy(() => import("./workspaces/ExpensesWorkspace"));
const AccountingWorkspace = lazy(() => import("./workspaces/AccountingWorkspace"));
const SettingsWorkspace = lazy(() => import("./workspaces/SettingsWorkspace"));
const CustomizeWorkspace = lazy(() => import("./workspaces/CustomizeWorkspace"));
const ComplianceWorkspace = lazy(() => import("./workspaces/ComplianceWorkspace"));
const EstimatesWorkspace = lazy(() => import("./sales/EstimatesWorkspace"));
const SalesOrdersWorkspace = lazy(() => import("./sales/SalesOrdersWorkspace"));
const SalesInvoicesWorkspace = lazy(() => import("./sales/SalesInvoicesWorkspace"));
const RecurringWorkspace = lazy(() => import("./sales/RecurringWorkspace"));
const SalesCreditNotesWorkspace = lazy(() => import("./sales/CreditNotesWorkspace"));
const ReportsWorkspace = lazy(() => import("./workspaces/ReportsWorkspace"));
const InventoryWorkspace = lazy(() => import("./workspaces/InventoryWorkspace"));
const BillingRulesWorkspace = lazy(() => import("./workspaces/BillingRulesWorkspace"));
const ProjectsWorkspace = lazy(() => import("./projects/ProjectsWorkspace"));
const VendorPortalWorkspace = lazy(() => import("./workspaces/VendorPortalWorkspace"));
const CustomModuleWorkspace = lazy(() => import("./workspaces/CustomModuleWorkspace"));
const ScanInboxWorkspace = lazy(() => import("./ai/ScanInboxWorkspace"));
const AdvancesWorkspace = lazy(() => import("./currency/Advances")); // wave 7: customer advances in any currency

/** What every screen gets from the shell. */
export type WorkspaceProps = {
  notify: (message: string) => void;
  /** The record open on screen (/leads/<id>), or null for the list. */
  openId: string | null;
  onOpen: (id: string | null) => void;
  go: (view: View, id?: string | null) => void;
};

export default function Shell() {
  const { staff, signOut, allow } = useAuth();
  const [{ view, id }, setRoute] = useState(() => viewFromPath(window.location.pathname));
  const [profileOpen, setProfileOpen] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number>(0);
  const profileRef = useRef<HTMLDivElement>(null);
  // The signed-in person's language and number/date formats (server/locale.js); the whole shell re-renders when they change.
  const loc = useLocale();
  const [mine, setMine] = useState<MyLocale | null>(null);
  // A reviewer's corrections to the language (Customization > Language & formats > Translations) come with it.
  const apply = (m: MyLocale) => {
    setMine(m);
    setLocale(m);
    if (m.language !== "en") void locale.overrides(m.language).then((o) => setOverrides(m.language, o.texts), () => undefined);
  };
  useEffect(() => {
    void locale.me().then(apply, () => undefined);
  }, []);
  const choose = (k: "language" | "number_format" | "date_format", v: string) =>
    void locale.saveMe({ [k]: v || null }).then(apply, (e: unknown) => setToast(e instanceof Error ? e.message : String(e)));

  useEffect(() => {
    const sync = () => setRoute(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const go = useCallback((next: View, nextId: string | null = null) => {
    const path = pathFor(next, nextId);
    if (path !== window.location.pathname) window.history.pushState(null, "", path);
    setRoute({ view: next, id: nextId });
    if (!nextId) window.scrollTo({ top: 0 });
  }, []);

  const notify = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3200);
  }, []);

  useEffect(() => {
    if (!profileOpen) return;
    const close = (e: MouseEvent) => !profileRef.current?.contains(e.target as Node) && setProfileOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [profileOpen]);

  const props: WorkspaceProps = { notify, openId: id, onOpen: (x) => go(view, x), go };
  const Screen = {
    dashboard: DashboardWorkspace,
    leads: LeadsWorkspace,
    onboarding: OnboardingWorkspace,
    clients: ClientsWorkspace,
    meetings: MeetingsWorkspace,
    campaigns: CampaignsWorkspace,
    announcements: AnnouncementsWorkspace,
    csi: CsiWorkspace,
    partners: PartnersWorkspace,
    billing: BillingWorkspace,
    subscriptions: SubscriptionsWorkspace,
    customers: CustomersWorkspace,
    items: ItemsWorkspace,
    purchases: PurchasesWorkspace,
    vendors: VendorsWorkspace,
    expenses: ExpensesWorkspace,
    accounting: AccountingWorkspace,
    settings: SettingsWorkspace,
    customize: CustomizeWorkspace,
    compliance: ComplianceWorkspace,
    estimates: EstimatesWorkspace,
    salesorders: SalesOrdersWorkspace,
    salesinvoices: SalesInvoicesWorkspace,
    recurring: RecurringWorkspace,
    salescredits: SalesCreditNotesWorkspace,
    reports: ReportsWorkspace,
    inventory: InventoryWorkspace,
    billingrules: BillingRulesWorkspace,
    projects: ProjectsWorkspace,
    vendorportal: VendorPortalWorkspace,
    modules: CustomModuleWorkspace,
    scaninbox: ScanInboxWorkspace,
    salesadvances: AdvancesWorkspace,
  }[view];
  // A role that cannot open this screen is told so, instead of seeing a list of errors (the server refuses the calls too).
  const blocked = !canSee(byView[view], allow);
  // Screens merged into one menu entry (routes.ts `under`) switch between each other from the top.
  const merged = family(view, allow);

  return (
    <div className="dashboard-shell">
      <a className="skip-link" href="#dashboard-main">
        {t("Skip to content")}
      </a>
      <Masthead active={view} activeId={id} onGo={(v, x) => go(v, x)} />

      <div className="app-frame">
        <header className="topbar">
          <div className="topbar__actions">
            <AiAssistant go={go} notify={notify} />
            <OrgSwitcher onManage={() => go("settings", "organizations")} />
            <div className="profile-wrap" ref={profileRef}>
              <button className="profile-button" aria-expanded={profileOpen} aria-label={t("Account menu")} onClick={() => setProfileOpen((o) => !o)}>
                <span>{initials(staff?.full_name)}</span>
                <span className="profile-button__copy">
                  <strong>{staff?.full_name ?? "—"}</strong>
                  <small>{staff?.email}</small>
                </span>
                <ChevronDown size={15} />
              </button>
              {profileOpen && (
                <div className="profile-menu">
                  <p className="crm-profile-who">
                    <strong>{staff?.full_name}</strong>
                    <small>{staff?.email}</small>
                    {staff?.role && <small>{t("Role: {role}", { role: tx(staff.role.name) })}</small>}
                  </p>
                  {mine && (
                    <div className="crm-profile-locale" onClick={(e) => e.stopPropagation()}>
                      <label>
                        <span>{t("Language")}</span>
                        <select value={mine.chosen.language ?? ""} onChange={(e) => choose("language", e.target.value)}>
                          <option value="">{t("Organisation default")}</option>
                          {Object.entries(mine.languages).map(([k, v]) => (
                            <option key={k} value={k}>
                              {langLabel(k, v)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>{t("Number format")}</span>
                        <select value={mine.chosen.number_format ?? ""} onChange={(e) => choose("number_format", e.target.value)}>
                          <option value="">{t("Organisation default")}</option>
                          {Object.entries(mine.number_formats).map(([k, v]) => (
                            <option key={k} value={k}>
                              {t(v)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>{t("Date format")}</span>
                        <select value={mine.chosen.date_format ?? ""} onChange={(e) => choose("date_format", e.target.value)}>
                          <option value="">{t("{t} ({example})", { t: t("Default"), example: dateFormatExample("dd mmm yyyy") })}</option>
                          {Object.entries(mine.date_formats).map(([k, v]) => (
                            <option key={k} value={k}>
                              {dateFormatExample(k) || v}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  <button className="profile-menu__signout" onClick={() => void signOut()}>
                    <LogOut size={15} />
                    {t("Sign out")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* One screen failing to render must not take the shell with it. */}
        <ErrorBoundary key={view}>
          <main className="dashboard-main" id="dashboard-main" lang={loc.language}>
            <PwaBanner notify={notify} go={go} />
            {blocked ? (
              <p className="crm-state" role="alert">
                {t("Your role ({role}) can't open {screen}. Ask an Admin if you need it.", { role: staff?.role?.name ?? t("none"), screen: t(byView[view].title) })}
              </p>
            ) : (
              <Suspense fallback={<Loading />}>
                {merged.length > 1 && (
                  <div className="crm-family">
                    <Segmented label="Also on this page" value={view} onChange={(v) => go(v)} options={merged.map((d) => ({ value: d.id, label: d.tab ?? d.title }))} />
                  </div>
                )}
                <Screen {...props} />
              </Suspense>
            )}
          </main>
        </ErrorBoundary>
      </div>

      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
